import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Stack, Typography, Checkbox, FormControlLabel, Chip, Divider,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { listAllSubjectsWithChapters } from 'src/axiosLib/services/subject/admin';

export default function SyllabusEditor({ open, onClose, syllabusEntries = [], onSave }) {
  const [allSubjects, setAllSubjects] = useState([]);
  // Map: { subjectId: Set of chapterIds }
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      setLoading(true);
      listAllSubjectsWithChapters((err, data) => {
        if (!err && Array.isArray(data)) {
          setAllSubjects(data);
        }
        setLoading(false);
      });
      // Initialize from existing syllabusEntries
      const map = {};
      (syllabusEntries || []).forEach(entry => {
        const sid = entry.subjectId?._id || entry.subjectId;
        if (sid) {
          map[sid] = new Set((entry.chapterIds || []).map(ch => ch._id || ch));
        }
      });
      setSelected(map);
    }
  }, [open]);

  const isSubjectSelected = (id) => selected[id] !== undefined;

  const toggleSubject = (subjectId, chapters) => {
    const copy = { ...selected };
    if (copy[subjectId] !== undefined) {
      delete copy[subjectId];
    } else {
      // Select subject with all chapters by default
      copy[subjectId] = new Set((chapters || []).map(ch => ch._id));
    }
    setSelected(copy);
  };

  const toggleChapter = (subjectId, chapterId) => {
    const copy = { ...selected };
    if (!copy[subjectId]) return;
    const chSet = new Set(copy[subjectId]);
    if (chSet.has(chapterId)) chSet.delete(chapterId);
    else chSet.add(chapterId);
    copy[subjectId] = chSet;
    setSelected(copy);
  };

  const selectAllChapters = (subjectId, chapters) => {
    const copy = { ...selected };
    copy[subjectId] = new Set((chapters || []).map(ch => ch._id));
    setSelected(copy);
  };

  const deselectAllChapters = (subjectId) => {
    const copy = { ...selected };
    copy[subjectId] = new Set();
    setSelected(copy);
  };

  const handleSave = () => {
    const entries = Object.entries(selected).map(([subjectId, chapterSet]) => ({
      subjectId,
      chapterIds: [...chapterSet],
    }));
    onSave(entries);
    onClose();
  };

  const totalSubjects = Object.keys(selected).length;
  const totalChapters = Object.values(selected).reduce((sum, s) => sum + s.size, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        📚 Select Subjects & Chapters for Syllabus
        <Typography sx={{ fontSize: '0.8rem', color: '#888', mt: 0.5 }}>
          Pick subjects then choose which chapters to include in this course.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ maxHeight: '65vh' }}>
        {loading ? (
          <Typography sx={{ py: 4, textAlign: 'center', color: '#888' }}>Loading subjects...</Typography>
        ) : allSubjects.length === 0 ? (
          <Typography sx={{ py: 4, textAlign: 'center', color: '#888' }}>
            No subjects found. Add subjects from the Institute page first.
          </Typography>
        ) : (
          allSubjects.map((subj) => {
            const isChecked = isSubjectSelected(subj._id);
            const chapters = subj.chapters || [];
            const selectedChapters = selected[subj._id] || new Set();
            const subjColor = subj.color || '#4338ca';
            const subjIcon = subj.icon || 'mdi:book-open-variant';

            return (
              <Box key={subj._id} sx={{
                mb: 1.5, borderRadius: 2,
                border: isChecked ? `2px solid ${subjColor}` : '1px solid #e5e7eb',
                background: isChecked ? `${subjColor}03` : '#fafafa',
                overflow: 'hidden', transition: 'all 0.2s',
              }}>
                {/* Subject Header */}
                <Box sx={{
                  px: 2, py: 1.5, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  '&:hover': { background: `${subjColor}06` },
                }}
                  onClick={() => toggleSubject(subj._id, chapters)}
                >
                  <Checkbox checked={isChecked}
                    sx={{ color: subjColor, '&.Mui-checked': { color: subjColor }, p: 0.5 }} />
                  <Box sx={{
                    width: 32, height: 32, borderRadius: 1.5, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    background: `${subjColor}15`,
                  }}>
                    <Icon icon={subjIcon} style={{ fontSize: 18, color: subjColor }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e1b4b' }}>
                      {subj.name}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Chip size="small" label={subj.subjectCode}
                      sx={{ fontSize: '0.68rem', background: '#f1f5f9' }} />
                    {isChecked && (
                      <Chip size="small"
                        label={`${selectedChapters.size}/${chapters.length}`}
                        sx={{ fontSize: '0.68rem', background: `${subjColor}15`, color: subjColor, fontWeight: 700 }} />
                    )}
                  </Stack>
                </Box>

                {/* Chapter List (only if subject selected) */}
                {isChecked && chapters.length > 0 && (
                  <Box sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Divider sx={{ mb: 1 }} />
                    <Stack direction="row" spacing={1} mb={1}>
                      <Button size="small" onClick={() => selectAllChapters(subj._id, chapters)}
                        sx={{ textTransform: 'none', fontSize: '0.72rem', color: subjColor }}>
                        Select All
                      </Button>
                      <Button size="small" onClick={() => deselectAllChapters(subj._id)}
                        sx={{ textTransform: 'none', fontSize: '0.72rem', color: '#999' }}>
                        Deselect All
                      </Button>
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.5 }}>
                      {chapters.map((ch) => (
                        <FormControlLabel key={ch._id}
                          control={
                            <Checkbox size="small" checked={selectedChapters.has(ch._id)}
                              onChange={() => toggleChapter(subj._id, ch._id)}
                              sx={{ color: subjColor, '&.Mui-checked': { color: subjColor }, p: 0.3 }} />
                          }
                          label={
                            <Typography sx={{ fontSize: '0.82rem', color: selectedChapters.has(ch._id) ? '#333' : '#888' }}>
                              {ch.chapterName}
                            </Typography>
                          }
                          sx={{ mx: 0, '&:hover': { background: '#f9f9f9' }, borderRadius: 1, px: 0.5 }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </DialogContent>
      <DialogActions>
        <Typography sx={{ flex: 1, pl: 2, fontSize: '0.8rem', color: '#888' }}>
          {totalSubjects} subject(s), {totalChapters} chapter(s) selected
        </Typography>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}
          sx={{ background: 'linear-gradient(135deg, #4338ca, #6366f1)', fontWeight: 700 }}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
